import { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import { getFnbEodInventory, saveFnbEodInventory } from '../../utilities/api'
import { PageLoadingContainer } from '../PageLoadingContainer'

export default function FnbEodReportInventoryModal({
  show,
  onHide,
  organizationUUID,
  eventUUID,
  locationId,
  reportDate,
  onSaved,
}) {
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (!show || !organizationUUID || !eventUUID || !locationId) {
      setItems([])
      return
    }

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    getFnbEodInventory(organizationUUID, {
      eventUUID,
      locationId,
      reportDate,
    })
      .then((res) => {
        if (cancelled) return
        setItems(Array.isArray(res?.data?.items) ? res.data.items : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load stand inventory', err)
        setLoadError('Could not load inventory items for this stand.')
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [show, organizationUUID, eventUUID, locationId, reportDate])

  const updateItem = (itemId, field, value) => {
    setItems((current) =>
      current.map((item) =>
        item.itemId === itemId
          ? {
              ...item,
              [field]: value === '' ? '' : Number(value),
            }
          : item
      )
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)

    try {
      const entries = items.map((item) => ({
        itemId: item.itemId,
        beginningQuantity: Number(item.beginningQuantity) || 0,
        receivedToday: Number(item.receivedToday) || 0,
        waste: Number(item.waste) || 0,
      }))

      await saveFnbEodInventory(organizationUUID, {
        eventUUID,
        locationId,
        reportDate,
        entries,
      })

      onSaved?.()
      onHide()
    } catch (err) {
      console.error('Failed to save stand inventory', err)
      setSaveError('Could not save inventory counts. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Stand Inventory Setup</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Enter opening counts, stock received during the event, and waste or
          spoilage. Ending inventory is calculated automatically on the report.
        </p>

        {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
        {saveError ? <Alert variant="danger">{saveError}</Alert> : null}

        {isLoading ? (
          <PageLoadingContainer style="without-sidebar" />
        ) : !items.length ? (
          <p className="mb-0 text-muted">
            No inventory-tracked menu items are assigned to this stand.
          </p>
        ) : (
          <div className="table-container border-bottom-0 fnb-eod-table-scroll">
            <Table responsive className="mb-0 fnb-eod-table">
              <thead>
                <tr>
                  <th className="text-left uppercase text-body">Item</th>
                  <th className="text-right uppercase text-body">Beginning</th>
                  <th className="text-right uppercase text-body">Received</th>
                  <th className="text-right uppercase text-body">Waste</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.itemId}>
                    <td className="text-left">{item.itemName}</td>
                    <td className="text-right">
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        size="sm"
                        className="fnb-eod-inventory-input"
                        value={item.beginningQuantity ?? 0}
                        onChange={(event) =>
                          updateItem(
                            item.itemId,
                            'beginningQuantity',
                            event.target.value
                          )
                        }
                      />
                    </td>
                    <td className="text-right">
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        size="sm"
                        className="fnb-eod-inventory-input"
                        value={item.receivedToday ?? 0}
                        onChange={(event) =>
                          updateItem(
                            item.itemId,
                            'receivedToday',
                            event.target.value
                          )
                        }
                      />
                    </td>
                    <td className="text-right">
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        size="sm"
                        className="fnb-eod-inventory-input"
                        value={item.waste ?? 0}
                        onChange={(event) =>
                          updateItem(item.itemId, 'waste', event.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Stack direction="horizontal" gap={2} className="ms-auto">
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || isLoading || !items.length}
          >
            {isSaving ? 'Saving…' : 'Save inventory'}
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  )
}
